import * as React from 'react'

import { CircleMinusIcon } from 'lucide-react'
import { inject, observer } from 'mobx-react'
import { classList } from 'react-classlist-helper'

import Avatar, {
  AvatarSize,
} from '~/client/src/shared/components/Avatar/Avatar'
import { Button } from '~/client/src/shared/components/Button/Button'
import { FilePicker } from '~/client/src/shared/components/FilePicker'
import { Loader } from '~/client/src/shared/components/Loader'
import AvatarType from '~/client/src/shared/enums/AvatarType'
import Localization from '~/client/src/shared/localization/LocalizationManager'
import { FileUploadingStore } from '~/client/src/shared/stores/domain/FileUploading.store'

import InitialState from '../../stores/InitialState'
import AvatarEditModal from './AvatarEditModal'
import AvatarInputStore from './AvatarInput.store'

import './AvatarInput.scss'

export type AvatarInputData = { url: string; initials?: string }

interface IProps {
  data: AvatarInputData
  onChange: (newUrl: string) => void
  withEditor?: boolean
  avatarType: AvatarType
  size?: AvatarSize
  fileUploadingStore?: FileUploadingStore
  state?: InitialState
  maxFileSize?: number
  maxWidth?: number
  maxHeight?: number
}

@inject('fileUploadingStore', 'state')
@observer
export default class AvatarInput extends React.Component<IProps> {
  private readonly store: AvatarInputStore = null
  private temporaryImageUrl: string = ''

  public constructor(props: IProps) {
    super(props)

    this.store = new AvatarInputStore(props.onChange, props.fileUploadingStore)
  }

  public render() {
    const { errorMessage, isLoading } = this.store
    const { withEditor: isCompact } = this.props

    if (isLoading) {
      return (
        <div className={classList({ 'avatar-loader-container': isCompact })}>
          <Loader />
        </div>
      )
    }

    if (errorMessage) {
      return <span className="text large red">{errorMessage}</span>
    }
    const {
      avatarType,
      data: { url, initials },
    } = this.props
    const { remove } = this.store

    return (
      <div
        className={classList({
          'row avatar-input-container': true,
          'compact-avatar': isCompact,
        })}
      >
        {!isCompact && (
          <Avatar src={url} initials={initials} avatarType={avatarType} />
        )}

        <FilePicker
          accept="image/*"
          onPick={
            isCompact ? this.displayAvatarEditor : this.handleChangeAvatar
          }
          className={
            isCompact
              ? 'tw:relative tw:block tw:cursor-pointer tw:overflow-hidden tw:rounded-full tw:border-0 tw:bg-transparent tw:p-0'
              : undefined
          }
          render={
            isCompact ? undefined : (
              <Button variant="link" className="tw:justify-start" />
            )
          }
        >
          {isCompact && (
            <Avatar
              src={url}
              initials={initials}
              avatarType={avatarType}
              size={this.props.size}
            />
          )}
          <span className={isCompact ? 'avatar-caption' : undefined}>
            {Localization.translator.change_verb}
          </span>
        </FilePicker>
        {!!url && (
          <span
            className="no-grow pointer remove-icon tw:flex"
            onClick={remove}
          >
            <CircleMinusIcon size={16} />
          </span>
        )}
        {this.store.isAvatarEditModalShown && this.renderAvatarEditPopUp()}
      </div>
    )
  }

  private handleChangeAvatar = ([file]: File[]) => {
    const {
      maxFileSize,
      maxWidth,
      maxHeight,
      state: {
        activeProject: { id: projectId },
      },
    } = this.props

    this.store.change(file, projectId, maxFileSize, maxWidth, maxHeight)
  }

  private displayAvatarEditor = ([file]: File[]) => {
    this.store.isAvatarEditModalShown = true
    this.temporaryImageUrl = URL.createObjectURL(file)
  }

  private renderAvatarEditPopUp = () => {
    return (
      <div className="avatar-edit-modal absolute-block">
        <AvatarEditModal
          store={this.store}
          image={this.temporaryImageUrl}
          projectId={this.props.state.activeProject.id}
        />
      </div>
    )
  }
}
